import React from "react";

class GlobalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    // Safe console logging for diagnostic observability
    try {
      console.error("GlobalErrorBoundary captured an uncaught error:", error, errorInfo);
    } catch {
      // Ignore logging failures in restricted environments
    }
  }

  handleReload = () => {
    try {
      window.location.reload();
    } catch {
      window.location.href = window.location.pathname;
    }
  };

  handleHome = () => {
    try {
      window.location.href = "/";
    } catch {
      window.location.assign("/");
    }
  };

  handleClearCacheAndHome = () => {
    try {
      window.localStorage.clear();
      window.sessionStorage.clear();
    } catch {
      // Ignore storage access errors
    }
    window.location.href = "/";
  };

  toggleDetails = () => {
    this.setState((prev) => ({ showDetails: !prev.showDetails }));
  };

  render() {
    if (this.state.hasError) {
      const errorMessage = this.state.error?.message || "An unexpected error occurred.";

      return (
        <div
          role="alert"
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
            backgroundColor: "#f8fafc",
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            color: "#0f172a",
          }}
        >
          <div
            style={{
              maxWidth: "520px",
              width: "100%",
              backgroundColor: "#ffffff",
              borderRadius: "16px",
              boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.04)",
              border: "1px solid #e2e8f0",
              padding: "36px 32px",
              textAlign: "center",
            }}
          >
            {/* Warning Icon Badge */}
            <div
              style={{
                width: "56px",
                height: "56px",
                borderRadius: "50%",
                backgroundColor: "#fef2f2",
                color: "#dc2626",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "20px",
                fontSize: "26px",
              }}
            >
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>

            <h1
              style={{
                fontSize: "22px",
                fontWeight: 700,
                color: "#0f172a",
                margin: "0 0 10px",
                letterSpacing: "-0.01em",
              }}
            >
              Something went wrong
            </h1>

            <p
              style={{
                fontSize: "14px",
                color: "#64748b",
                margin: "0 0 28px",
                lineHeight: "1.5",
              }}
            >
              The application encountered an unexpected issue while loading this page. You can reload
              the page or return to the homepage.
            </p>

            {/* Action Buttons */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                marginBottom: "20px",
              }}
            >
              <button
                type="button"
                onClick={this.handleReload}
                style={{
                  width: "100%",
                  padding: "12px 18px",
                  borderRadius: "10px",
                  backgroundColor: "#4f46e5",
                  color: "#ffffff",
                  fontSize: "14px",
                  fontWeight: 600,
                  border: "none",
                  cursor: "pointer",
                  transition: "background-color 0.15s ease",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = "#4338ca";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = "#4f46e5";
                }}
              >
                Reload Page
              </button>

              <button
                type="button"
                onClick={this.handleHome}
                style={{
                  width: "100%",
                  padding: "11px 18px",
                  borderRadius: "10px",
                  backgroundColor: "#ffffff",
                  color: "#334155",
                  fontSize: "14px",
                  fontWeight: 600,
                  border: "1px solid #cbd5e1",
                  cursor: "pointer",
                  transition: "background-color 0.15s ease",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = "#f8fafc";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = "#ffffff";
                }}
              >
                Return to Homepage
              </button>

              <button
                type="button"
                onClick={this.handleClearCacheAndHome}
                style={{
                  background: "none",
                  border: "none",
                  color: "#94a3b8",
                  fontSize: "12px",
                  cursor: "pointer",
                  padding: "6px",
                  textDecoration: "underline",
                }}
              >
                Clear local session data & reload
              </button>
            </div>

            {/* Collapsible Error Diagnostics */}
            <div style={{ marginTop: "16px", borderTop: "1px solid #f1f5f9", paddingTop: "12px" }}>
              <button
                type="button"
                data-testid="toggle-error-details"
                onClick={this.toggleDetails}
                style={{
                  background: "none",
                  border: "none",
                  color: "#64748b",
                  fontSize: "12px",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <span>{this.state.showDetails ? "Hide technical details" : "Show technical details"}</span>
              </button>

              {this.state.showDetails && (
                <div
                  style={{
                    marginTop: "10px",
                    textAlign: "left",
                    backgroundColor: "#f8fafc",
                    padding: "12px",
                    borderRadius: "8px",
                    fontSize: "11px",
                    color: "#475569",
                    maxHeight: "160px",
                    overflowY: "auto",
                    fontFamily: "monospace",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  <strong>Error:</strong> {errorMessage}
                  {this.state.errorInfo?.componentStack && (
                    <>
                      <br />
                      <br />
                      <strong>Component Stack:</strong>
                      {this.state.errorInfo.componentStack}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default GlobalErrorBoundary;
